'use client';

import { Grid, List, Stack, Text, Title } from '@mantine/core';

const PrivacyNoticeContent = () => {
  return (
    <Grid.Col
      span={8} // Has to be 8 to match the Grid distribution in the FancyTOCWrapper
      id="content"
    >
      <Stack gap="lg">
        <Stack gap="md">
          <Title
            order={2}
            id="changes-to-this-privacy-notice"
          >
            1. CHANGES TO THIS PRIVACY NOTICE
          </Title>
          <Text size="md">
            We may modify this Privacy Notice from time to time, in which case
            we will update the “Last Updated” date at the top of this Privacy
            Notice. If we make material changes to how we use or disclose
            information we collect, we will use reasonable efforts to notify you
            as required by applicable law. If you do not agree to any updates to
            this Privacy Notice, please do not continue using or accessing the
            Services.
          </Text>
        </Stack>

        <Stack gap="md">
          <Title
            order={2}
            id="collection-and-use-of-information"
          >
            2. COLLECTION AND USE OF INFORMATION
          </Title>
          <Text size="md">
            When you use or access the Services, we collect certain categories
            of information about you from a variety of sources. In addition to
            the specific uses discussed below, we may use this information to
            provide and improve the Services and to maintain our business
            relationship, including by enhancing the safety and security of our
            Services, providing customer support, complying with applicable
            legal obligations, enforcing any applicable terms of service, and
            protecting our rights and the rights of our employees, users or
            other individuals.
          </Text>

          <br />

          <Title
            order={3}
            id="information-you-provide-to-us"
          >
            <i>Information You Provide to Us.</i>
          </Title>

          <Text size="md">
            Some features of the Services may require you to directly provide
            certain information, including information about yourself. You may
            elect not to provide this information, but doing so may prevent you
            from creating issue pages, voting on issues, or otherwise using or
            accessing certain features. Information that you directly submit
            through our Services includes:
          </Text>

          <List
            type="unordered"
            spacing={'md'}
            withPadding
          >
            <List.Item>
              <Text size="md">
                <strong>Basic contact details and identifiers</strong>, such as
                name, address, phone number, email address, date of birth, and
                state issued ID, that you provide when you sign up for the
                Services as a Leader or Supporter. We use this information to
                create and maintain your account, verify your identity and voter
                registration / eligibility status, and provide the Services, and
                to communicate with you (including to tell you about products or
                services that may be of interest to you).
              </Text>
            </List.Item>
            <List.Item>
              <Text size="md">
                <strong>Account information</strong>, such as username,
                password, and passkeys. We use this information to provide the
                Services (including so you can create issue pages and vote on
                issues) and to maintain and secure your account with us
                (including through two-factor authentication). If you choose to
                register an account, you are responsible for keeping your
                account credentials safe. We recommend you do not share your
                access details with anyone else. If you believe your account has
                been compromised, please contact us immediately.
              </Text>
            </List.Item>
            <List.Item>
              <Text size="md">
                <strong>Profile information</strong>, such as your name,
                organization, headline, and photo (should you choose to provide
                it). We use this information to provide the Services, including
                so you can create issue pages as a Leader and vote on issues as
                a Supporter.
              </Text>
            </List.Item>
            <List.Item>
              <Text size="md">
                <strong>User-content</strong>, such as [issue/topic preferences,
                personal stances or slogans, and other issue-related
                information], submitted by you when you create and populate
                issue pages as a Leader. We or others may store, display,
                reproduce, publish, or otherwise use such content, and may or
                may not attribute it to you. Others may also have access to such
                content and may have the ability to share it with third parties.
                For example, if you choose to create an issue page, the issue
                page may be shared and accessed by anyone with a link to the
                page, including us.
              </Text>
            </List.Item>
            <List.Item>
              <Text size="md">
                <strong>Your interactions with the Services</strong>, including
                how you choose to vote on an issue as a Supporter (should you
                choose to vote on an issue and provide this information).
              </Text>
            </List.Item>
            <List.Item>
              <Text size="md">
                <strong>
                  Any other information you choose to include in communications
                  with us
                </strong>
                , for example, when sending a message through the Services.
              </Text>
            </List.Item>
          </List>

          <br />

          <Title
            order={3}
            id="information-collected-automatically"
          >
            <i>Information Collected Automatically.</i>
          </Title>

          <Text size="md">
            We and third parties may also use cookies, pixels or other tracking
            technologies to automatically collect certain information about your
            interactions with the Services. This information is collected and
            used to tailor your experience with the Services, run analytics,
            better understand user interactions with the Services, etc. Such
            information includes:
          </Text>

          <List
            type="unordered"
            spacing={'md'}
            withPadding
          >
            <List.Item>
              <Text size="md">
                <strong>Device information</strong>, such as device type,
                operating system, unique device identifier, and internet
                protocol (IP) address.
              </Text>
            </List.Item>
            <List.Item>
              <Text size="md">
                <strong>Location information</strong>, such as approximate
                location / precise geolocation, if you choose to provide it.
              </Text>
            </List.Item>
            <List.Item>
              <Text size="md">
                <strong>
                  Other information regarding your interaction with the Services
                </strong>
                , such as browser type, log data, date and time stamps,
                clickstream data, interactions with marketing emails, and ad
                impressions.
              </Text>
            </List.Item>
          </List>

          <Text size="md">
            Most browsers accept cookies automatically, but you may be able to
            control the way in which your devices permit the use of cookies. If
            you so choose, you may block or delete certain of our cookies from
            your browser; however, blocking or deleting cookies may cause some
            of the Services, including any portal features and general
            functionality, to work incorrectly.
          </Text>

          <Text size="md">
            Your browser settings may also allow you to transmit a “Do Not
            Track” signal when you visit various websites. Like many websites,
            our website is not designed to respond to “Do Not Track” signals
            received from browsers.
          </Text>

          <br />

          <Title
            order={3}
            id="information-collected-from-other-sources"
          >
            <i>Information Collected From Other Sources.</i>
          </Title>

          <Text size="md">
            We may obtain information about you from outside sources, including
            information that we collect directly from third parties and
            information from third parties that you choose to share with us.
            Such information includes:
          </Text>

          <List
            type="unordered"
            spacing={'md'}
            withPadding
          >
            <List.Item>
              <Text size="md">
                <strong>Analytics data</strong> we receive from analytics
                providers such as Google Analytics and Sentry.
              </Text>
            </List.Item>
            <List.Item>
              <Text size="md">
                <strong>
                  Information about whether you are registered / eligible to
                  vote
                </strong>{' '}
                we receive from third-party providers such as TargetSmart.
              </Text>
            </List.Item>
            <List.Item>
              <Text size="md">
                <strong>
                  Information we receive when you choose to link any social
                  media platforms to your account
                </strong>
                , such as Facebook or X, which we use to maintain your account
                and login information.
              </Text>
            </List.Item>
          </List>

          <Text size="md">
            Any information we receive from outside sources will be treated in
            accordance with this Privacy Notice. We are not responsible for the
            accuracy of the information provided to us by third parties and are
            not responsible for any third party’s policies or practices. For
            more information, see the section below, Third-Party Websites and
            Links.
          </Text>
          <br />
          <Title
            order={3}
            id="deidentified-information"
          >
            <i>Deidentified Information.</i>
          </Title>
          <Text size="md">
            Finally, we may deidentify or anonymize your information such that
            it cannot reasonably be used to infer information about you or
            otherwise be linked to you (or we may collect information that has
            already been deidentified/anonymized), and we may use such
            deidentified/anonymized information for any purpose.
          </Text>
        </Stack>

        <Stack gap="md">
          <Title
            order={2}
            id="disclosure-of-information"
          >
            3. DISCLOSURE OF INFORMATION
          </Title>
          <Text size="md">
            We may disclose your information for legitimate purposes subject to
            this Privacy Notice, including the following categories of third
            parties:
          </Text>
          <List
            type="unordered"
            spacing={'md'}
            withPadding
          >
            <List.Item>
              <Text size="md">
                Vendors or other service providers who help us provide the
                Services, including for system administration, authentication,
                voter verification, ballot information, cloud storage and
                database provision, security, customer relationship management,
                and web analytics.
              </Text>
            </List.Item>
            <List.Item>
              <Text size="md">
                Third parties to whom you request or direct us to disclose
                information, such as through your use of social media widgets or
                login integration.
              </Text>
            </List.Item>
            <List.Item>
              <Text size="md">
                Professional advisors, such as auditors, law firms, or
                accounting firms.
              </Text>
            </List.Item>
            <List.Item>
              <Text size="md">
                Third parties in connection with or anticipation of an asset
                sale, merger, bankruptcy, or other business transaction.
              </Text>
            </List.Item>
          </List>

          <Text size="md">
            We may also disclose your information as needed to comply with
            applicable law or any obligations thereunder or to cooperate with
            law enforcement, judicial orders, and regulatory inquiries, to
            enforce any applicable terms of service, and to ensure the safety
            and security of our business, employees, and users.
          </Text>
        </Stack>

        <Stack gap="md">
          <Title
            order={2}
            id="social-features"
          >
            4. SOCIAL FEATURES
          </Title>
          <Text size="md">
            Certain features of the Services allow you to initiate interactions
            between the Services and third-party services or platforms, such as
            social networks (“
            <strong>Social Features</strong>”). Social Features may include
            features that allow you to access our pages on third-party
            platforms, and from there to “like” or “share” our content. Use of
            Social Features may allow a third party to collect and/or use your
            information. If you use Social Features, information you post or
            make accessible may be publicly displayed by the third-party
            service. Both we and the third party may have access to information
            about you and your use of both the Services and the third-party
            service. For more information, see the section,{' '}
            <i>Third-Party Websites and Links</i>.
          </Text>
        </Stack>

        <Stack gap="md">
          <Title
            order={2}
            id="third-party-websites-and-links"
          >
            5. THIRD-PARTY WEBSITES AND LINKS
          </Title>
          <Text size="md">
            We may provide links to third-party websites or platforms. If you
            follow links to sites or platforms that we do not control and are
            not affiliated with us, you should review the applicable privacy
            notice, policies and other terms. We are not responsible for the
            privacy or security of, or information found on, these sites or
            platforms. Information you provide on public or semi-public venues,
            such as third-party social networking platforms, may also be
            viewable by other users of the Services and/or users of those
            third-party platforms without limitation as to its use. Our
            inclusion of such links does not, by itself, imply any endorsement
            of the content on such platforms or of their owners or operators.
          </Text>
        </Stack>

        <Stack gap="md">
          <Title
            order={2}
            id="data-security-and-retention"
          >
            6. DATA SECURITY AND RETENTION
          </Title>
          <Text size="md">
            Despite our reasonable efforts to protect your information, no
            security measures are impenetrable, and we cannot guarantee “perfect
            security.” Any information you send to us electronically, while
            using the Services or otherwise interacting with us, may not be
            secure while in transit. We recommend that you do not use unsecure
            channels to send us sensitive or confidential information.
            <br />
            We retain your information for as long as is reasonably necessary
            for the purposes specified in this Privacy Notice. When determining
            the length of time to retain your information, we consider various
            criteria, including whether we need the information to continue to
            provide you the Services, resolve a dispute, enforce our contractual
            agreements, prevent harm, promote safety, security and integrity, or
            protect ourselves, including our rights, property or products.
          </Text>
        </Stack>

        <Stack gap="md">
          <Title
            order={2}
            id="privacy-rights"
          >
            7. PRIVACY RIGHTS
          </Title>
          <Text size="md">
            California residents under the age of 18 who have registered to use
            the Services and have posted content or information on the Services
            can request that such information be removed from the Services by
            contacting us at the email address below. Such request must state
            that the California resident personally posted such content or
            information and detail where the content or information is posted.
            We will make reasonable good faith efforts to remove the post or
            content from prospective public view or anonymize it so the minor
            cannot be individually identified. This removal process cannot
            ensure complete or comprehensive removal. For instance, third
            parties may have republished the post, and archived copies of it may
            be stored by search engines and others that we do not control.
          </Text>
        </Stack>

        <Stack gap="md">
          <Title
            order={2}
            id="how-to-contact-us"
          >
            8. HOW TO CONTACT US
          </Title>
          <Text size="md">
            Should you have any questions about our privacy practices or this
            Privacy Notice, please email us at{' '}
            <a
              href="mailto:privacy@sway.co"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              privacy@sway.co
            </a>{' '}
          </Text>
        </Stack>
      </Stack>
    </Grid.Col>
  );
};

export { PrivacyNoticeContent };
